import React, { Suspense, lazy } from 'react';
import { canLoadPDFJS } from '../../utils/pdfFeatureDetection';
import PdfPreviewFallback from './PDFPreviewFallback';

interface PdfPreviewProps {
  pdfUrl: string;
  width?: number;
  height?: number;
}

// Lazy load the actual PDFPreview component only if the browser supports it
// The import function checks feature support before attempting the import
const PdfPreviewActual = canLoadPDFJS()
  ? lazy(() => {
      // Double-check feature support before importing (defensive)
      if (!canLoadPDFJS()) {
        // Return a rejected promise to prevent import
        return Promise.reject(new Error('Browser does not support required JavaScript features'));
      }
      return import('./PDFPreview');
    })
  : null;

/**
 * Lazy-loaded wrapper for PDFPreview that checks browser compatibility
 * before attempting to load pdfjs-dist. Falls back to a simple UI
 * for browsers that cannot parse modern JavaScript.
 */
const PdfPreviewLazy: React.FC<PdfPreviewProps> = (props) => {
  // If browser doesn't support modern JS, show fallback immediately
  if (!canLoadPDFJS() || !PdfPreviewActual) {
    return <PdfPreviewFallback {...props} />;
  }

  // Otherwise, lazy load the actual component
  return (
    <Suspense
      fallback={
        <div
          style={{ width: props.width || 300, height: props.height || 400 }}
          className="flex items-center justify-center border border-gray-300 rounded-lg bg-gray-50"
        >
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      }
    >
      <PdfPreviewActual {...props} />
    </Suspense>
  );
};

export default PdfPreviewLazy;

